import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { type SubmitEvent, useState } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { Button } from "@/components/ui/Button";
import { Field } from "@/components/ui/Field";
import { authClient } from "@/lib/authClient";

const searchSchema = z.object({ token: z.string().min(1) });

export const Route = createFileRoute("/definir-contrasena")({
  validateSearch: searchSchema,
  component: SetPasswordPage,
});

// FormData.get() puede devolver un File, así que String(...) directo dispara
// no-base-to-string. Los campos de este formulario son siempre de texto.
function fieldValue(data: FormData, name: string): string {
  const value = data.get(name);
  return typeof value === "string" ? value : "";
}

function SetPasswordPage() {
  const { t } = useTranslation();
  const { token } = Route.useSearch();
  const navigate = useNavigate();
  const [error, setError] = useState<string>();

  async function handleSubmit(event: SubmitEvent<HTMLFormElement>) {
    event.preventDefault();
    const data = new FormData(event.currentTarget);
    const password = fieldValue(data, "password");
    const confirm = fieldValue(data, "confirm");

    if (password.length < 12) {
      setError(t("auth.passwordTooShort"));
      return;
    }
    if (password !== confirm) {
      setError(t("auth.passwordsDontMatch"));
      return;
    }

    // El alta no pasa por el plugin organization: el administrador crea la cuenta y
    // Better Auth envía un enlace con este token para definir la contraseña. Sirve
    // igual para un alta nueva que para un olvido.
    const { error: resetError } = await authClient.resetPassword({ newPassword: password, token });
    if (resetError) {
      setError(t("common.error"));
      return;
    }

    // "/" es el layout autenticado (_auth): la Task 20 le agrega la pantalla
    // de bienvenida. Ver la nota equivalente en entrar.tsx.
    await navigate({ to: "/" });
  }

  return (
    <div className="grid min-h-dvh place-items-center bg-bg px-4">
      <div className="w-full max-w-[360px] rounded-[3px] border border-line bg-card p-8">
        <p className="text-[15px] font-black tracking-[0.06em] text-bur">
          {t("app.brand")}
        </p>
        <h1 className="mt-6">{t("auth.acceptTitle")}</h1>
        <p className="mt-1 mb-6 text-nota text-ink3">{t("auth.acceptSubtitle")}</p>

        <form onSubmit={(event) => void handleSubmit(event)} className="flex flex-col gap-4">
          <Field
            label={t("auth.newPassword")}
            name="password"
            type="password"
            autoComplete="new-password"
            required
          />
          <Field
            label={t("auth.confirmPassword")}
            name="confirm"
            type="password"
            autoComplete="new-password"
            required
          />
          {error && (
            <p role="alert" className="text-nota text-warn">
              {error}
            </p>
          )}
          <Button type="submit">{t("auth.accept")}</Button>
        </form>
      </div>
    </div>
  );
}
